import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hexagon, ArrowLeft, ScreenShare, ScreenShareOff, MonitorPlay, Loader2,
  Volume2, VolumeX, Type, Gamepad2, Zap, AlertTriangle,
} from "lucide-react";
import Orb from "@/components/Orb";
import { useVoice } from "@/hooks/useVoice";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

const SPEEDS = [
  { id: "fast", label: "Fast", floor: 1200 },
  { id: "balanced", label: "Balanced", floor: 4000 },
  { id: "chill", label: "Relaxed", floor: 9000 },
];

export default function Coach() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { speak, stopSpeaking } = useVoice();

  const [sharing, setSharing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [tips, setTips] = useState([]);
  const [latest, setLatest] = useState("");
  const [game, setGame] = useState("");
  const [speed, setSpeed] = useState("fast");
  const [output, setOutput] = useState("both"); // spoken | text | both
  const [err, setErr] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const runningRef = useRef(false);
  const lastTipRef = useRef("");
  const gameRef = useRef("");
  const speedRef = useRef("fast");
  const outputRef = useRef("both");

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);
  useEffect(() => { gameRef.current = game; }, [game]);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { outputRef.current = output; }, [output]);

  const supported = typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;

  const captureFrame = useCallback(() => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return null;
    const maxW = 1280;
    const scale = Math.min(1, maxW / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.6);
  }, []);

  const analyzeLoop = useCallback(async () => {
    while (runningRef.current) {
      const started = Date.now();
      const frame = captureFrame();
      if (!frame) { await new Promise((r) => setTimeout(r, 500)); continue; }
      setAnalyzing(true);
      try {
        const token = localStorage.getItem("archon_token");
        const res = await fetch(`${API}/coach/analyze`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ image_base64: frame, game: gameRef.current, last_tip: lastTipRef.current }),
        });
        if (res.ok) {
          const data = await res.json();
          const tip = (data.tip || "").trim();
          if (tip && runningRef.current) {
            lastTipRef.current = tip;
            setLatest(tip);
            setTips((t) => [{ tip, at: new Date().toLocaleTimeString() }, ...t].slice(0, 30));
            if (outputRef.current !== "text") speak(tip);
          }
        } else if (res.status === 401 || res.status === 403) {
          setErr("Session expired — please sign in again.");
          runningRef.current = false;
        }
      } catch { /* transient network error, keep looping */ } finally { setAnalyzing(false); }
      if (!runningRef.current) break;
      const floor = SPEEDS.find((s) => s.id === speedRef.current)?.floor ?? 0;
      const wait = Math.max(0, floor - (Date.now() - started));
      if (wait) await new Promise((r) => setTimeout(r, wait));
    }
  }, [captureFrame, speak]);

  const start = async () => {
    setErr("");
    if (!supported) { setErr("Screen sharing isn't available in this browser. Use a desktop browser like Chrome or Edge."); return; }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      stream.getVideoTracks()[0].addEventListener("ended", stop);
      setSharing(true);
      runningRef.current = true;
      analyzeLoop();
    } catch (e) {
      setErr("Screen share was cancelled or blocked.");
    }
  };

  const stop = () => {
    runningRef.current = false;
    stopSpeaking();
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setSharing(false);
    setAnalyzing(false);
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  return (
    <div className="min-h-screen bg-[#050505] grid-bg">
      <div className="fixed inset-0 glow-radial pointer-events-none" />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-blue-900/30 backdrop-blur">
        <div className="flex items-center gap-3">
          <button onClick={() => { stop(); navigate(-1); }} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn"><ArrowLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <MonitorPlay className="text-cyan-400" size={18} />
            <div>
              <p className="font-display font-semibold leading-tight">Live Coach</p>
              <p className="mono-label">{sharing ? (analyzing ? "READING YOUR SCREEN…" : "WATCHING") : "OFFLINE"}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sharing ? (
            <button data-testid="stop-share-btn" onClick={stop}
              className="flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium transition-all">
              <ScreenShareOff size={16} /> Stop
            </button>
          ) : (
            <button data-testid="start-share-btn" onClick={start}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium transition-all hover:shadow-[0_0_22px_rgba(59,130,246,0.5)]">
              <ScreenShare size={16} /> Share Screen
            </button>
          )}
        </div>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-5 gap-6">
        {/* Left: controls + preview */}
        <div className="lg:col-span-2 space-y-5">
          {/* Settings */}
          <div className="glass rounded-2xl p-5 space-y-5" data-testid="coach-settings">
            <div>
              <label className="mono-label flex items-center gap-2 mb-2"><Gamepad2 size={13} /> Game (optional)</label>
              <input data-testid="game-input" value={game} onChange={(e) => setGame(e.target.value)}
                placeholder="e.g. Valorant, League, Fortnite…"
                className="w-full bg-slate-950/60 border border-blue-900/50 focus:border-cyan-400 outline-none rounded-lg px-3 py-2.5 text-sm text-slate-50" />
            </div>
            <div>
              <p className="mono-label mb-2">Reaction Speed</p>
              <div className="grid grid-cols-3 gap-2">
                {SPEEDS.map((s) => (
                  <button key={s.id} data-testid={`speed-${s.id}`} onClick={() => setSpeed(s.id)}
                    className={`text-sm rounded-lg py-2 border transition-all ${speed === s.id ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-blue-900/40 text-slate-400 hover:bg-white/5"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mono-label mb-2">Feedback Output</p>
              <div className="grid grid-cols-3 gap-2">
                {[{ id: "spoken", icon: Volume2, l: "Voice" }, { id: "text", icon: Type, l: "Text" }, { id: "both", icon: Zap, l: "Both" }].map((o) => (
                  <button key={o.id} data-testid={`output-${o.id}`} onClick={() => { setOutput(o.id); if (o.id === "text") stopSpeaking(); }}
                    className={`flex items-center justify-center gap-1.5 text-sm rounded-lg py-2 border transition-all ${output === o.id ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-blue-900/40 text-slate-400 hover:bg-white/5"}`}>
                    <o.icon size={14} /> {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="glass rounded-2xl p-3">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-video grid place-items-center">
              <video ref={videoRef} muted playsInline className={`w-full h-full object-contain ${sharing ? "" : "hidden"}`} data-testid="screen-preview" />
              {!sharing && (
                <div className="text-center text-slate-500 px-6">
                  <MonitorPlay size={34} className="mx-auto mb-3 opacity-60" />
                  <p className="text-sm">Your shared screen preview appears here.</p>
                </div>
              )}
              {sharing && analyzing && (
                <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-black/60 rounded-full px-2.5 py-1 text-[0.65rem] text-cyan-300 font-mono">
                  <Loader2 size={11} className="animate-spin" /> analyzing
                </div>
              )}
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />
          {err && <p data-testid="coach-error" className="text-red-400 text-sm border border-red-500/30 bg-red-500/5 rounded-lg px-4 py-3 flex items-start gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5" />{err}</p>}
          <p className="text-xs text-slate-600">Desktop only. Tip: share the game window or tab for best results. Feedback is near-real-time (a fresh read every few seconds).</p>
        </div>

        {/* Right: live feedback */}
        <div className="lg:col-span-3 space-y-5">
          <div className="glass rounded-2xl p-6 min-h-[180px] flex flex-col" data-testid="latest-tip">
            <div className="flex items-center gap-3 mb-4">
              <Orb state={analyzing ? "thinking" : sharing ? "speaking" : "idle"} size={64} />
              <div>
                <p className="mono-label">ARCHON — LIVE READ</p>
                <p className="text-xs text-slate-500 mt-0.5">{sharing ? "Coaching active" : "Start sharing to begin"}</p>
              </div>
              <button onClick={() => setOutput(output === "text" ? "both" : "text")} className="ml-auto text-slate-500 hover:text-cyan-300" title="Toggle voice">
                {output === "text" ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>
            <AnimatePresence mode="wait">
              <motion.p key={latest} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="font-display text-xl sm:text-2xl leading-snug text-slate-50 flex-1">
                {latest || (sharing ? "Reading your game…" : "Awaiting your screen.")}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Log */}
          <div>
            <p className="mono-label mb-3">FEED HISTORY</p>
            <div className="space-y-2 max-h-[46vh] overflow-y-auto -mr-2 pr-2" data-testid="tip-log">
              {tips.map((t, i) => (
                <motion.div key={t.at + i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                  className="glass rounded-lg px-4 py-3 flex gap-3" data-testid={`log-${i}`}>
                  <span className="mono-label text-cyan-400 shrink-0">{t.at}</span>
                  <span className="text-sm text-slate-300 leading-relaxed">{t.tip}</span>
                </motion.div>
              ))}
              {tips.length === 0 && <p className="text-slate-600 text-sm">Coaching callouts will stream here.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
